import { useState, type FormEvent } from 'react'
import { useToast } from 'grabbitbot-legacy-ui'
import { screenerClient } from '../../api'
import type { AdminRecord } from '../../api/types'

/** Equivalent of legacy `AdminUserCreditInfoComponent` — BUSINESS-CRITICAL
 * (audit §4/§6: view/add/remove a user's credit balance, direct monetary
 * balance mutation). Kept exactly to the legacy contract (`cause`/
 * `reference` free-text fields, no additional confirmation UI beyond what
 * legacy had — audit didn't call for a stronger confirmation flow here as a
 * "defect", only noted the absence; adding one would be new UX, not parity,
 * so this stays a straight port). */
export function AdminUserCreditPage() {
  const { showToast } = useToast()
  const [email, setEmail] = useState('')
  const [currency, setCurrency] = useState('USD')
  const [credit, setCredit] = useState<AdminRecord | null>(null)
  const [amount, setAmount] = useState('')
  const [cause, setCause] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)

  async function lookup(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      const result = await screenerClient.admin.viewCredit(email, currency)
      setCredit(result)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not load credit', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function adjust(direction: 'add' | 'remove') {
    const numericAmount = Number(amount)
    if (!email || !cause || !Number.isFinite(numericAmount) || numericAmount <= 0) {
      showToast('Email, a positive amount, and a cause are required', 'error')
      return
    }
    setBusy(true)
    try {
      const payload = { email, currency, amount: numericAmount, cause, reference: reference || undefined }
      const result = direction === 'add' ? await screenerClient.admin.addCredit(payload) : await screenerClient.admin.removeCredit(payload)
      setCredit(result)
      showToast(`Credit ${direction === 'add' ? 'added' : 'removed'}`, 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Credit adjustment failed', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <h1>Admin — user credit</h1>
      <form className="screener-form" onSubmit={lookup}>
        <label>
          User email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label>
          Currency
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} required />
        </label>
        <button type="submit" disabled={busy}>
          View balance
        </button>
      </form>

      {credit != null && (
        <div className="screener-card">
          <h2>Balance</h2>
          <pre>{JSON.stringify(credit, null, 2)}</pre>
        </div>
      )}

      <div className="screener-card">
        <h2>Adjust balance</h2>
        <div className="screener-form">
          <label>
            Amount
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
          <label>
            Cause
            <input value={cause} onChange={(e) => setCause(e.target.value)} />
          </label>
          <label>
            Reference (optional)
            <input value={reference} onChange={(e) => setReference(e.target.value)} />
          </label>
          <div className="screener-actions">
            <button type="button" disabled={busy} onClick={() => adjust('add')}>
              Add credit
            </button>
            <button type="button" disabled={busy} onClick={() => adjust('remove')}>
              Remove credit
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
